<template>
  <!-- User Access Dialog for Vehicle Scrapers -->
  <v-dialog
    v-model="dialogVisible"
    width="600"
  >
    <v-card>
      <v-card-title class="text-h5 grey lighten-2">
        Manage User Access for {{ scraper ? scraper.name : '' }}
      </v-card-title>
      <v-card-text style="margin-top:30px;">
        <v-select
          v-model="accessType"
          :items="['All', 'None', 'Custom']"
          label="Access Type"
          @change="handleAccessTypeChange"
        ></v-select>

        <v-select
          v-if="accessType === 'Custom'"
          v-model="selectedUsers"
          :items="users"
          item-text="name"
          item-value="user_id"
          label="Select Users"
          multiple
          chips
          :loading="loading"
        >
          <template v-slot:prepend-item>
            <v-list-item>
              <v-list-item-content>
                <v-text-field v-model="searchTerm" placeholder="Search" @input="searchUsers"></v-text-field>
              </v-list-item-content>
            </v-list-item>
            <v-divider class="mt-2"></v-divider>
          </template>
        </v-select>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          text
          @click="saveAccess"
          :disabled="loading"
        >
          Save
        </v-btn>
        <v-btn
          color="primary"
          text
          @click="close"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from 'vue';

export default {
  name: 'UserAccessDialog',
  props: {
    value: Boolean,
    scraper: Object
  },
  data() {
    return {
      users: [],
      filteredUsers: [],
      selectedUsers: [],
      accessType: 'All',
      loading: false,
      searchTerm: '',
      originalUsers: []
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    }
  },
  watch: {
    scraper: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.id) {
          this.loadUserAccess();
          this.loadScraperSettings();
        }
      }
    }
  },
  methods: {
    loadUserAccess() {
      if (!this.scraper) return;

      this.loading = true;
      axios({
        method: "GET",
        url: `/api/vehicle-scrapers/${this.scraper.id}/user-access`,
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Authorization': "Bearer " + localStorage.getItem('token')
        }
      })
      .then(response => {
        if (response.data && response.data.data) {
          this.users = response.data.data;
          this.originalUsers = [...this.users];
          this.filteredUsers = [...this.users];

          // Extract users with access
          this.selectedUsers = this.users
            .filter(user => user.has_access)
            .map(user => user.user_id);
        } else {
          Vue.$toast.error('Failed to load user profiles', {
            position: 'top-right'
          });
        }
        this.loading = false;
      })
      .catch(err => {
        Vue.$toast.error('Error loading user profiles: ' + err.message, {
          position: 'top-right'
        });
        console.error(err);
        this.loading = false;
      });
    },

    loadScraperSettings() {
      if (!this.scraper) return;

      this.loading = true;
      axios({
        method: "GET",
        url: `/api/vehicle-scrapers/${this.scraper.id}/settings`,
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Authorization': "Bearer " + localStorage.getItem('token')
        }
      })
      .then(response => {
        if (response.data && response.data.data) {
          const settings = response.data.data;
          this.accessType = settings.access_type || 'All';
          if (settings.access_type === 'Custom' && settings.selected_users) {
            this.selectedUsers = settings.selected_users;
          }
        }
        this.loading = false;
      })
      .catch(err => {
        console.error('Error loading scraper settings:', err);
        this.loading = false;
      });
    },

    handleAccessTypeChange() {
      if (this.accessType === 'All') {
        this.selectedUsers = this.users.map(user => user.user_id);
      } else if (this.accessType === 'None') {
        this.selectedUsers = [];
      }
    },

    searchUsers() {
      if (!this.searchTerm) {
        this.filteredUsers = this.originalUsers;
        return;
      }

      const term = this.searchTerm.toLowerCase();
      this.filteredUsers = this.originalUsers.filter(user => {
        return user.name.toLowerCase().includes(term) ||
               user.email.toLowerCase().includes(term);
      });
    },

    saveAccess() {
      if (!this.scraper) return;

      this.loading = true;

      // Prepare data based on access type
      let data = {
        scraper_id: this.scraper.id,
        access_type: this.accessType,
        selected_users: this.accessType === 'Custom' ? this.selectedUsers : []
      };

      axios({
        method: "POST",
        url: "/api/vehicle-scrapers/update-access-settings",
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Authorization': "Bearer " + localStorage.getItem('token')
        },
        data: data
      })
      .then(response => {
        if (response.data && response.data.success) {
          Vue.$toast.success('User access settings updated', {
            position: 'top-right'
          });
          this.close();
        } else {
          Vue.$toast.error('Failed to update user access settings', {
            position: 'top-right'
          });
        }
        this.loading = false;
      })
      .catch(err => {
        Vue.$toast.error('Error updating user access settings: ' + err.message, {
          position: 'top-right'
        });
        console.error(err);
        this.loading = false;
      });
    },

    close() {
      this.dialogVisible = false;
    }
  }
};
</script>
